<script lang="ts">
  import Button from '$lib/components/Button.svelte';
  import H from '$lib/components/H.svelte';
  import LoadingSpinner from '$lib/components/LoadingSpinner.svelte';
  import TextField from '$lib/components/TextField.svelte';
  import type { ClientUserStore } from 'shared/src';
  import { onDestroy, onMount } from 'svelte';
  import { autorun } from 'mobx';
  import countries from '../../../components/payments/countryCodes';

  import PreviewLicenseDetails from './PreviewLicenseDetails.svelte';
  import Radio from '$lib/components/Radio.svelte';
  import Select from '$lib/components/Select.svelte';
  import Option from '$lib/components/Option.svelte';
  import { errorProvider } from '$lib/error-handling/errorProvider';
  import { API } from 'shared/src/utils/api/api';
  import { getApiPaths } from '$lib/stores/getUserStore';
  import { goto } from '$app/navigation';

  export let licenseType: 'group' | 'testing' = 'group';
  export let quantity: number = 10;
  export let starts: Date = new Date();
  export let months: 6 | 12 = 12;
  export let countryCode: string = 'US';
  export let postalCode: string = '';
  export let doneRoute = '/app/teach/licenses';

  export let clientUserStore: ClientUserStore | undefined = undefined;

  $: api = clientUserStore ? clientUserStore.api : new API(getApiPaths());

  let clazz = '';
  export { clazz as class };

  let firstName: string = '';
  let lastName: string = '';
  let schoolName: string = '';
  let email: string = '';
  let userId: string = '';
  let isTaxExempt: boolean = false;

  const destroy = autorun(() => {
    if (!clientUserStore) return;
    firstName = firstName || clientUserStore.firstName || '';
    lastName = lastName || clientUserStore.lastName || '';
    schoolName = schoolName || clientUserStore.schoolName || '';
    email = email || clientUserStore.email || '';
    userId = clientUserStore.id || '';
  });

  onDestroy(destroy);

  let quoteError: any = {};

  let state: 'awaitingInput' | 'gettingQuote' | 'done' = 'awaitingInput';

  let showErrorsEvenWhenFocused = false;
  let isValid = false;
  let errorMessage = '';
  function getQuote(evt: Event) {
    evt.preventDefault();
    const body = {
      firstName,
      lastName,
      schoolName,
      email,
      userId,
      taxExempt: isTaxExempt,
      licenseType,
      quantity,
      starts,
      months,
      location: {
        countryCode,
        postalCode,
      },
    };
    errorMessage = '';
    quoteError = {};
    api
      .$post('/api/quotes', body)
      .then(() => {
        state = 'done';
      })
      .catch((err) => {
        state = 'awaitingInput';
        quoteError = err?.errors || {};
        errorMessage = err?.message || 'Sorry, there was a problem getting that quote.';
        errorProvider.error(err);
      });
  }
  onMount(() => {
    onChange();
  });
  let form: HTMLFormElement;

  function onChange() {
    isValid = form.checkValidity();
  }
</script>

<div class={clazz}>
  <H level={2} class="card-padding">Get Quote</H>
  <PreviewLicenseDetails {licenseType} {quantity} {starts} {months} on:backToEditDetails />
  <div class="p-2 col-span-full bg-orange relative">
    <H level={3} class="text-white text-xl font-medium text-center px-4">Quote Details:</H>
  </div>
  {#if state === 'awaitingInput' || state === 'gettingQuote'}
    <div class="card-padding">
      <form
        class="text-base"
        bind:this={form}
        on:submit={(evt) => {
          evt.preventDefault();
        }}
      >
        <div class="sm:flex sm:flex-wrap">
          <TextField
            class="sm:w-1/2 px-2"
            on:change={onChange}
            on:input={onChange}
            bind:value={firstName}
            label="First name"
            autocomplete="given-name"
            required
            localValidationError={!!quoteError.firstName}
            customValidityMessage={quoteError.firstName ? quoteError.firstName : ''}
            showErrorWhenFocused={showErrorsEvenWhenFocused}
            showErrorWhenPristine={showErrorsEvenWhenFocused}
          />
          <TextField
            class="sm:w-1/2 px-2"
            on:change={onChange}
            on:input={onChange}
            bind:value={lastName}
            label="Last name"
            autocomplete="family-name"
            required
            localValidationError={!!quoteError.lastName}
            customValidityMessage={quoteError.lastName ? quoteError.lastName : ''}
            showErrorWhenFocused={showErrorsEvenWhenFocused}
            showErrorWhenPristine={showErrorsEvenWhenFocused}
          />
          <TextField
            class="sm:w-1/2 px-2"
            on:change={onChange}
            on:input={onChange}
            type="email"
            bind:value={email}
            label="Email"
            autocomplete="email"
            required
            localValidationError={!!quoteError.email}
            customValidityMessage={quoteError.email
              ? quoteError.email
              : '' || 'Please enter a valid email.'}
            showErrorWhenFocused={showErrorsEvenWhenFocused}
            showErrorWhenPristine={showErrorsEvenWhenFocused}
          />
          <TextField
            class="sm:w-1/2 px-2"
            on:change={onChange}
            on:input={onChange}
            bind:value={schoolName}
            label="School name"
            autocomplete="organization"
            required
            localValidationError={!!quoteError.schoolName}
            customValidityMessage={quoteError.schoolName ? quoteError.schoolName : ''}
            showErrorWhenFocused={showErrorsEvenWhenFocused}
            showErrorWhenPristine={showErrorsEvenWhenFocused}
          />

          <TextField
            class="sm:w-1/2 px-2"
            label="Postal Code"
            bind:value={postalCode}
            on:change={onChange}
            on:input={onChange}
            type="text"
            id="zip-input"
            placeholder="90210"
            localValidationError={!!quoteError.location?.postalCode}
            customValidityMessage={quoteError.location?.postalCode
              ? quoteError?.location?.postalCode
              : ''}
            showErrorWhenFocused={showErrorsEvenWhenFocused}
            showErrorWhenPristine={showErrorsEvenWhenFocused}
          />
          <div class="px-2 sm:w-1/2">
            <Select
              class="w-full input rounded-md h-fit py-3"
              on:change={onChange}
              on:input={onChange}
              bind:value={countryCode}
              id="country-selector"
            >
              {#each countries as country}
                <Option value={country.alpha2}>
                  {country.shortName || country.altName || country.name}
                </Option>
              {/each}
            </Select>
          </div>
          <div class="p-2">
            <Radio class="my-2" name="tax-exempt" value={false} bind:group={isTaxExempt}
              >Our organization is <strong>not</strong> tax exempt</Radio
            >
            <Radio class="my-2" name="tax-exempt" value={true} bind:group={isTaxExempt}
              >Our organization <strong>is</strong> tax exempt (more documentation may be required)</Radio
            >
          </div>
          <input type="hidden" bind:value={userId} />
        </div>
        {#if errorMessage}
          <div class="text-red">{errorMessage}</div>
        {/if}
        <!-- 
          -- IMPORTANT: we omit type="submit" because of the bug below
          -- https://github.com/tailwindlabs/tailwindcss/issues/6602
          -- until it is fixed, background color classes don't work on buttons
          -- with their type specified.
          -->
        <Button
          disabled={!isValid}
          on:click={getQuote}
          on:disabledClick={() => {
            showErrorsEvenWhenFocused = true;
          }}
          class="block w-1/2 min-w-max h-12 text-sm font-normal mx-auto mt-4"
        >
          <span class="px-4 xs:px-20">
            {#if state !== 'gettingQuote'}
              Get Quote
            {:else}
              <LoadingSpinner />
            {/if}
          </span></Button
        >
      </form>
    </div>
  {:else if state === 'done'}
    <div class="p-2 col-span-full bg-green relative">
      <H level={3} class="text-white text-xl font-medium text-center px-4">Success!</H>
    </div>
    <div class="card-padding text-lg pb-8">
      <p class="my-2">
        We have received your request for a quote, and will email you in the next 24-48 hours with a
        quote.
      </p>
      <p class="my-2">
        In the interim, please feel free to reach out to us at sales@utheory.com with any questions!
      </p>
      <div class="text-center">
        <Button
          class="mx-auto my-8 inline-block"
          on:click={() => {
            goto(doneRoute);
          }}>Done</Button
        >
      </div>
    </div>
  {/if}
</div>
